import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { store } from "../store";
import { logout } from "../store/reducers/authSlice";
import { notify } from "../utils/notification";
import {
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
  removeTokens,
  isTokenExpired,
} from "../utils/authentication";
import { startApiLoading, stopApiLoading } from "../utils/loading";

// Import NetworkStatus notifications - lazy import to avoid circular dependencies
let notifyServerDown: (() => void) | null = null;
let notifyServerOnline: (() => void) | null = null;

const loadNetworkNotifications = async () => {
  if (!notifyServerDown || !notifyServerOnline) {
    try {
      const networkModule = await import("../components/NetworkStatus");
      notifyServerDown = networkModule.notifyServerDown;
      notifyServerOnline = networkModule.notifyServerOnline;
    } catch (error) {
      console.warn("Could not load network notifications:", error);
    }
  }
};

interface ApiErrorResponse {
  errors: {
    statusCode: number;
    message: string;
    error: string;
  };
  message?: string;
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:3000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout
});

// Stores ongoing token refresh promise
let refreshTokenPromise: Promise<string> | null = null;

const refreshAuthToken = async (): Promise<string> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/refresh`,
      { refreshToken },
    );

    const { token, newRefreshToken } = response.data;
    setAuthToken(token);
    setRefreshToken(newRefreshToken);
    return token;
  } catch (error) {
    removeTokens();
    store.dispatch(logout());
    throw error;
  }
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Customize loading message based on the request
    let loadingMessage = "Processing request...";

    if (config.method === "get") {
      loadingMessage = "Loading data...";
    } else if (config.method === "post") {
      loadingMessage = "Saving data...";
    } else if (config.method === "put" || config.method === "patch") {
      loadingMessage = "Updating data...";
    } else if (config.method === "delete") {
      loadingMessage = "Deleting data...";
    }

    startApiLoading(loadingMessage);

    let token = getAuthToken();

    if (token && isTokenExpired(token)) {
      try {
        // Create or reuse existing refresh promise
        refreshTokenPromise = refreshTokenPromise || refreshAuthToken();
        token = await refreshTokenPromise;
        refreshTokenPromise = null;
      } catch (error) {
        refreshTokenPromise = null;
        throw error;
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    stopApiLoading();
    notify.error({
      message: "Request Failed",
      description: "Failed to send request. Please check your connection.",
    });
    return Promise.reject(error);
  },
);

// Response interceptor
axiosInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    stopApiLoading();

    // Load network notifications if not already loaded
    await loadNetworkNotifications();

    // If we get a successful response, notify that server is back online
    if (notifyServerOnline) {
      notifyServerOnline();
    }

    // if (
    //   response.config.url?.includes("user/sign-in") &&
    //   response.status === 200 &&
    //   response.data?.data?.firstName
    // ) {
    //   notify.success({
    //     message: "Welcome back!",
    //     description: `Successfully logged in as ${response.data.data.firstName} ${response.data.data.lastName}.`,
    //   });
    // }

    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    stopApiLoading();
    const originalRequest = error.config;

    // Load network notifications if not already loaded
    await loadNetworkNotifications();

    // Handle network errors (server disconnected, no internet, etc.)
    if (!error.response) {
      // Notify about server being down (unless it's a connectivity check)
      if (
        !originalRequest?.headers?.["x-connectivity-check"] &&
        notifyServerDown
      ) {
        notifyServerDown();
      }

      // Check if this is a critical request that needs user notification
      const isCriticalRequest =
        originalRequest?.url?.includes("/auth/") ||
        originalRequest?.headers?.["x-critical"] === "true";

      if (isCriticalRequest) {
        notify.error({
          message: "Network Error",
          description: "Please check your internet connection.",
        });
      }

      // Always reject but don't show notification for non-critical requests
      return Promise.reject(error);
    }

    // Get error message from response data

    const errorMessage =
      error.response.data?.errors?.message ||
      error.response.data?.message ||
      "An unexpected error occurred";

    // Map status codes to human readable titles
    const statusCodeMessages: Record<number, string> = {
      400: "Bad Request",
      401: "Authentication Failed",
      403: "Access Denied",
      404: "Not Found",
      409: "Conflict Error",
      422: "Validation Error",
      500: "Server Error",
      502: "Bad Gateway",
      503: "Service Unavailable",
      504: "Gateway Timeout",
    };

    const errorType =
      statusCodeMessages[error.response.status] ||
      `Error ${error.response.status}`;

    // Handle 401 (Unauthorized) errors specially
    if (error.response.status === 401) {
      if (!originalRequest?.headers["x-retry"] && !getAuthToken()) {
        // Login attempt failed
        notify.error({
          message: "Authentication Failed",
          description:
            error.response.data?.errors?.message || "Invalid credentials",
        });
      } else if (originalRequest && !originalRequest.headers["x-retry"]) {
        try {
          // Create or reuse existing refresh promise
          refreshTokenPromise = refreshTokenPromise || refreshAuthToken();
          const newToken = await refreshTokenPromise;
          refreshTokenPromise = null;

          // Retry the original request with new token
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          originalRequest.headers["x-retry"] = "true";
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          refreshTokenPromise = null;
          notify.error({
            message: "Session Expired",
            description: "Please log in again to continue.",
          });
          store.dispatch(logout());
        }
      } else {
        notify.error({
          message: "Authentication Failed",
          description:
            error.response.data?.errors?.message ||
            "Session expired. Please log in again.",
        });
        store.dispatch(logout());
      }
    } else {
      // Handle all other errors
      notify.error({
        message: errorType,
        description: errorMessage,
      });
    }

    return Promise.reject(error);
  },
);

// Exporting both the axios instance and the loading utilities
export { startApiLoading, stopApiLoading };
export default axiosInstance;
